import type { Node } from 'unist';

import { MARK_CODE } from '@gitbook/doc-core';
import type { Leaf, Mark, Text } from '@gitbook/slate';

interface TransformTextToNodeOptions {
    /** Create nodes for a text break */
    transformTextBreak?: () => Node[];
    /** Transform a mark */
    transformMark?: (mark: Mark, children: Node[]) => Node[];
    /** Transform the text in each leaves */
    transformText?: (text: string) => string;
}

/**
 * Transform a Text node into a generic nodes.
 * This function is used both for GAST to HTML and Markdown
 */
export function transformTextToNode(text: Text, options: TransformTextToNodeOptions = {}): Node[] {
    // new-lines in leaf blocks are stored as "\n" in GitBook
    // while we need break node for the unified parser
    const lines = text.splitBy('\n');

    const transformedNodes: Node[] = [];
    for (let index = 0; index < lines.length; index += 1) {
        const line = lines[index];
        const leaves = line.getLeaves().toArray();

        transformedNodes.push(
            ...leaves.flatMap((leaf, index) =>
                transformLeaf(leaves[index - 1], leaf, leaves[index + 1], options)
            )
        );

        if (options.transformTextBreak && index < lines.length - 1) {
            transformedNodes.push(...options.transformTextBreak());
        }
    }

    return optimizeTextTree(transformedNodes);
}

/**
 * Transform a Leaf node, simply returning the text.
 */
function transformLeaf(
    previousLeaf: Leaf | undefined,
    leaf: Leaf,
    nextLeaf: Leaf | undefined,
    options: TransformTextToNodeOptions
): Node[] {
    const marks = sortMarksForLeaf(leaf);
    const rawTextValue = options.transformText ? options.transformText(leaf.text) : leaf.text;

    const justText = [
        {
            type: 'text',
            value: rawTextValue,
        },
    ];

    if (marks.length === 0) {
        return justText;
    }

    // Marks containing only spaces are problematic. E.g. <bold> <bold> => ** **
    // Remark will not convert these to markdown, instead the control characters
    // will get encoded and be visible in the final markdown.
    if (marks.length > 0 && rawTextValue.trim() === '') {
        return justText;
    }

    // Markdown doesn't work well trailing/leading spaces in marks
    // so we move them as text nodes before/after the node.
    let before: Node[] = [];
    let after: Node[] = [];

    const leadingSpaces = getLeadingSpaces(rawTextValue);
    const trailingSpaces = getTrailingSpaces(rawTextValue);

    const previousLeafMarks = previousLeaf ? sortMarksForLeaf(previousLeaf) : [];
    const nextLeafMarks = nextLeaf ? sortMarksForLeaf(nextLeaf) : [];

    if (
        leadingSpaces &&
        (previousLeafMarks.length > 0 || getTrailingSpaces(previousLeaf?.text || '').length === 0)
    ) {
        before = transformTextWithMarks(
            leadingSpaces,
            getCommonMarks(previousLeafMarks, marks),
            options
        );
    }
    if (
        trailingSpaces &&
        (nextLeafMarks.length > 0 || getLeadingSpaces(nextLeaf?.text || '').length === 0)
    ) {
        after = transformTextWithMarks(
            trailingSpaces,
            getCommonMarks(marks, nextLeafMarks),
            options
        );
    }

    return [...before, ...transformTextWithMarks(rawTextValue.trim(), marks, options), ...after];
}

function transformTextWithMarks(
    textValue: string,
    marks: Mark[],
    options: TransformTextToNodeOptions
): Node[] {
    let result: Node[] = [
        {
            type: 'text',
            // @ts-expect-error
            value: textValue,
        },
    ];

    if (options.transformMark) {
        for (let index = 0; index < marks.length; index += 1) {
            const mark = marks[index];
            result = options.transformMark(mark, result);
        }
    }

    return result;
}

/**
 * Sort the marks in the order they should be applied.
 */
function sortMarksForLeaf(leaf: Leaf): Mark[] {
    return leaf.marks.toArray().sort((a, b) => {
        // code should be first (deepest in the tree)
        if (a.type === MARK_CODE) {
            return -1;
        }
        if (b.type === MARK_CODE) {
            return 1;
        }

        return a.type.localeCompare(b.type);
    });
}

/**
 * Extract all common marks between 2 sets.
 */
function getCommonMarks(marks: Mark[], otherMarks: Mark[]): Mark[] {
    return marks.filter((mark) => otherMarks.some((otherMap) => otherMap.equals(mark)));
}

/**
 * Merge consecutive nodes that are the same (eg. both bold for example).
 * `remark-stringify` doesn't handle them well and it just makes a nicer AST overall.
 */
function optimizeTextTree<NodeType extends Node>(nodes: NodeType[]): NodeType[] {
    const result: NodeType[] = [];

    let last: NodeType | undefined;
    for (let index = 0; index < nodes.length; index += 1) {
        const node = nodes[index];
        if (
            last &&
            node.type === last.type &&
            // @ts-expect-error
            (node.type === 'element' ? node.tagName === last.tagName : true) &&
            // @ts-expect-error
            (node.type === 'text' || Array.isArray(node.children))
        ) {
            // merge the nodes
            if (last.type === 'text') {
                // @ts-expect-error
                last.value += node.value;
            } else {
                // @ts-expect-error
                last.children = last.children.concat(node.children);
            }

            // try merging with the next node as well
            continue;
        }

        // last node is ready, we can optimize it as well
        // @ts-expect-error
        if (last && Array.isArray(last.children)) {
            // @ts-expect-error
            last.children = optimizeTextTree(last.children);
        }

        // let's push the current node to the stack and try to optimize it now
        last = node;
        result.push(node);
    }

    return result;
}

function getTrailingSpaces(text: string): string {
    return text.match(/\s*$/)?.[0] || '';
}

function getLeadingSpaces(text: string): string {
    return text.match(/^\s*/)?.[0] || '';
}
